<template>
  <div>
    <v-col class="col-12 p-0">
      <div v-for="(question, index) in questions" :key="index">
        <v-col v-if="questionTypeChoix(question)" cols="12" class="p-0 pb-2" :key="index">
          <select-field :ref="'GRAPH_QUESTION'+index"
                        :value="getValue(question)"
                        :items="getChoix(question)"
                        :disabled=disabled
                        :editFieldInfo="getSelectInfo(question)"
                        :required=true
                        @updated="(item) => choixChange(question, item)">
          </select-field>
        </v-col>
        <v-col v-if="questionTypeEntier(question)" cols="12" class="p-0" :key="index">
          <edit-field :ref="'GRAPH_QUESTION'+index"
                      :value="getEntier(question)"
                      :editFieldInfo="getEditInfo(question)"
                      :required=true
                      :disabled=disabled
                      @updated="(value) => entierChange(question, value)">
          </edit-field>
        </v-col>
      </div>
    </v-col>
  </div>
</template>

<script lang="ts">
import { EditFieldType, FieldInfo, FieldStatus } from '@/framework/base/components/FieldInfo';
import EditField from '@/framework/base/components/EditField.vue';
import Item from '@/framework/base/classes/Item';
import GraphData, { GraphAnswer } from '@/generated/classes/GraphData';
import GraphQuestion, { GraphQuestionTypes } from '@/generated/classes/GraphQuestion';
import { MapRessource } from '@/framework/base/helpers/Ressources';
import { ObjTypes } from '@/generated/classes/ObjTypes';
import SelectField, { SELECT_TEXT } from '@/framework/base/components/SelectField.vue';
import DialogHubManager from "@/framework/dialog/helpers/DialogHubManager";
import { RessourceTypes } from '@/framework/ressource/classes/Ressource';
import { defineComponent, PropType } from "vue";

export default defineComponent({
  components: {
    EditField,
    SelectField
  },
  props: {
    hideTitle: {
      type: Boolean
    },
    dialog: {
      type: Boolean
    },
    title: {
      type: String
    },
    toggle: {
      type: Boolean
    },
    objType: {
      type: Number,
      required: true
    },
    firstQuestion: {
      type: String
    },
    obj: {
      type: Object as PropType<any>
    },
    value: {
      type: Array as PropType<GraphAnswer[]>,
      required: true
    },
    disabled: {
      type: Boolean
    }
  },
  created() {
    const hubKey = 'graph-' + this.objType.toString() + '-' + this.obj.id
    DialogHubManager.waitAnswer(hubKey, this.getQuestions);
  },
  data() {
    const initValue: GraphAnswer[] = [];

    return {
      lastToggle: false,
      lastContextId: undefined,
      graphData: new GraphData(),
      initValue,
      status: new FieldStatus(false, false, 0)
    };
  },
  computed: {
    graph(): GraphData {
      if (this.toggle != this.lastToggle || this.obj.id != this.lastContextId) {
        this.updateGraphData();
      }
      return this.graphData;
    },
    questions(): GraphQuestion[] {
      return this.graph.questions.filter(a => !a.calcul);
    }
  },

  methods: {
    updateGraphData() {
      this.lastContextId = this.obj.id;
      this.lastToggle = this.toggle;
      this.initValue = JSON.parse(JSON.stringify(this.value));
      this.graphData = new GraphData();
      this.graphData.obj = JSON.stringify(this.obj);
      this.graphData.firstQuestion = this.firstQuestion ? this.firstQuestion : '';
      this.graphData.objType = this.objType;
      this.graphData.objId = this.obj.id;
      this.process();
    },
    getQuestions() {
      this.process();
      setTimeout(() => this.touch(), 300);
    },
    mask(value: string): string {
      return '';
    },
    getSelectInfo(question: GraphQuestion): FieldInfo {
      const ressource = new MapRessource();
      ressource.id = 0;
      ressource.type = RessourceTypes.GRAPH_QUESTION;
      ressource.name = question.convention + '-' + question.code;
      ressource.helpHtmlId = question.aideHtmlId;
      ressource.label = question.label;
      const type = EditFieldType.SUMMARY;
      const precision = 2;
      return new FieldInfo(ressource, type, precision, this.mask, null, null, null);
    },
    getEditInfo(question: GraphQuestion): FieldInfo {
      const ressource = new MapRessource();
      ressource.id = 0;
      ressource.type = RessourceTypes.GRAPH_QUESTION;
      ressource.name = question.convention + '-' + question.code;
      ressource.helpHtmlId = question.aideHtmlId;
      ressource.label = question.label;
      const type = EditFieldType.INT;
      const precision = 2;
      return new FieldInfo(ressource, type, precision, this.mask, null, null, null);
    },
    questionTypeChoix(question: GraphQuestion): boolean {
      return (question.type == GraphQuestionTypes.CHOIX ||
        question.type == GraphQuestionTypes.BOOL) &&
        question.choix.length > 1;
    },
    questionTypeEntier(question: GraphQuestion): boolean {
      return question.type == GraphQuestionTypes.ENTIER;
    },
    getChoix(question: GraphQuestion): Item[] {
      const res: Item[] = [];
      question.choix.forEach((choix, index) => {
        res.push(new Item(index + 1, choix.label));
      });
      if (question.sortChoix) {
        res.sort((a, b) => a.title > b.title ? 1 : -1);
      }
      return res;
    },
    getValue(question: GraphQuestion): string {
      let res = SELECT_TEXT;
      if (question.answer) {
        const valueLabel = this.getChoix(question).find(a => a.title == question.answer.label);
        if (valueLabel) {
          res = valueLabel.title;
        } else {
          const value = this.getChoix(question).find(a => a.title == question.answer.value);
          if (value) {
            res = value.title;
          }
        }
      }
      return res;
    },
    getEntier(question: GraphQuestion): string {
      let res = '';
      if (question.answer) {
        res = question.answer.value;
      }
      return res;
    },
    setUpdated(newValues: GraphAnswer[]): void {
      this.status.error = false;
      this.status.updated = false;
      this.graphData.questions.filter(a => !a.calcul).forEach((question) => {
        if (!question.answer) {
          this.status.error = true;
        }
      })
      if (newValues.length != this.initValue.length) {
        this.status.updated = true;
      } else {
        newValues.forEach((answer) => {
          const initValue = this.initValue.find(a => a.code == answer.code);
          if (!initValue || initValue.value != answer.value || initValue.label != answer.label) {
            this.status.updated = true;
          }
        })
      }
      this.$emit('update:value', newValues);
      this.$emit('status', this.status);
    },
    choixChange(question: GraphQuestion, item: Item) {
      this.clearQuestions(question);
      const value = question.choix.find(a => a.label == item.title);
      if (value) {
        question.answer = new GraphAnswer();
        question.answer.code = question.code;
        question.answer.donnee = question.donnee;
        question.answer.value = value.value;
        question.answer.label = value.label;
      }
      this.updateAnswers(question, item.title, question.answer.value);
    },
    entierChange(question: GraphQuestion, value: string) {
      this.clearQuestions(question);
      question.answer = new GraphAnswer();
      question.answer.code = question.code;
      question.answer.donnee = question.donnee;
      question.answer.value = value;
      this.updateAnswers(question, '', value);
    },
    updateAnswers(question: GraphQuestion, text: string, value: string) {
      const newValues: GraphAnswer[] = [];
      this.graphData.questions.forEach((question) => {
        if (this.value) {
          const oldValue = this.value.find(a => a.code == question.code);
          if (oldValue) {
            newValues.push(oldValue);
          }
        }
      });
      const answer = newValues.find(a => a.code == question.code);
      if (answer) {
        answer.donnee = question.donnee;
        answer.label = text;
        answer.value = value;
      } else {
        const graphAnswer = new GraphAnswer();
        graphAnswer.code = question.code;
        graphAnswer.donnee = question.donnee;
        graphAnswer.label = text;
        graphAnswer.value = value;
        newValues.push(graphAnswer);
      }
      this.setUpdated(newValues);
      this.process();
    },
    clearQuestions(question: GraphQuestion) {
      const index = this.graphData.questions.indexOf(question);
      if (index >= 0) {
        for (let i = this.graphData.questions.length - 1; i > index; i--) {
          this.graphData.questions.splice(i);
        }
      }
    },
    process() {
      GraphData.process(this.graphData).then((res) => {
        this.graphData = res;
        const answers: GraphAnswer[] = [];
        this.graphData.questions.forEach((question) => {
          if (question.answer) {
            answers.push(question.answer);
          }
        })
        this.setUpdated(answers);
        this.$emit('setMinimum', this.graphData.minimum);
        this.$emit('hasGraph', this.graphData.graphId > 0);
      })
    },
    missing(): boolean {
      let missing = false;
      this.questions.forEach((question, index) => {
        if (this.$refs['GRAPH_QUESTION' + index]) {
          // @ts-expect-error : generated method
          missing = missing || (this.$refs['GRAPH_QUESTION' + index].length > 0 && this.$refs['GRAPH_QUESTION' + index][0].missing());
        }
      })
      return missing;
    },
    touch(): void {
      let error = false;
      this.questions.forEach((question, index) => {
        if (this.$refs['GRAPH_QUESTION' + index]) {
          // @ts-expect-error : generated method
          error = error || (this.$refs['GRAPH_QUESTION' + index].length > 0 && this.$refs['GRAPH_QUESTION' + index][0].touch());
        }
      })
      this.status.error = error;
      this.$emit('status', this.status);
    }
  }
})

</script>
